"use client";

import React from "react";
import TrophyIcon from "@/components/icons/TrophyIcon";
import TrashIcon from "@/components/icons/TrashIcon";
import { ContestService } from "@/services/contest/contest-service";
import ContestPeriodLabel from "../ContestPeriodLabel";
import { CategoryChips } from "../category/CategoryChips";
import { ContestResponseData } from "@/restapi/contest";
import { i18nKey } from "@/lib/react/i18n/types/locale";
import Toggle from "@/components/ui/toggle";
import { IoMdLock } from "react-icons/io";

type Props = {
  contest: ContestResponseData;
  href: string;
  t: (key: i18nKey) => string;
  locale: "ja" | "en";

  canShowDeleteUI: boolean;
  sending: boolean;
  openDeleteModal: (contest: ContestResponseData) => void;

  bulkMode?: boolean;
  checked?: boolean;
  onToggleCheck?: (contestId: number) => void;

  onTogglePublish?: (contest: ContestResponseData, next: boolean) => void;
  publishSaving?: boolean;

  disableLink?: boolean;
  mode?: "published" | "settings";
};

const ContestRow = ({
  contest,
  href,
  t,
  locale,
  canShowDeleteUI,
  sending,
  openDeleteModal,
  bulkMode = false,
  checked = false,
  onToggleCheck,
  onTogglePublish,
  publishSaving = false,
  disableLink = false,
  mode = "published",
}: Props) => {
  const handleToggleCheck = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleCheck?.(contest.id);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    openDeleteModal(contest);
  };

  const isRowClickDisabled = bulkMode || disableLink;
  const showActionUI = canShowDeleteUI && !bulkMode;

  const rowGridClass = (() => {
    if (bulkMode && showActionUI) {
      return "grid-cols-[auto_auto_minmax(0,1fr)] lg:grid-cols-[auto_auto_minmax(0,1fr)_180px_auto]";
    }
    if (bulkMode) {
      return "grid-cols-[auto_auto_minmax(0,1fr)] lg:grid-cols-[auto_auto_minmax(0,1fr)_180px]";
    }
    if (showActionUI) {
      return "grid-cols-[auto_minmax(0,1fr)] lg:grid-cols-[auto_minmax(0,1fr)_180px_auto]";
    }
    return "grid-cols-[auto_minmax(0,1fr)] lg:grid-cols-[auto_minmax(0,1fr)_180px]";
  })();

  const anchorClassName = [
    "grid gap-x-3 gap-y-2 py-4 transition",
    rowGridClass,
    isRowClickDisabled ? "cursor-not-allowed opacity-95" : "cursor-pointer",
  ].join(" ");

  return (
    <li className="border-b border-white/15">
      <div
      >
        <a
          href={isRowClickDisabled ? undefined : href}
          aria-disabled={isRowClickDisabled}
          onClick={(e) => {
            if (bulkMode) {
              e.preventDefault();
              e.stopPropagation();
              onToggleCheck?.(contest.id);
              return;
            }

            if (disableLink) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          className={anchorClassName}
        >
          {bulkMode && (
            <button
              type="button"
              aria-label="select contest"
              onClick={handleToggleCheck}
              className={[
                "mt-1 w-5 h-5 rounded border",
                checked ? "bg-white/80 border-white/60" : "bg-transparent border-white/25",
                "flex items-center justify-center",
                "hover:bg-white/10",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30",
              ].join(" ")}
            >
              {checked && <span className="text-black text-[12px] leading-none">✓</span>}
            </button>
          )}

          <div className="pt-1">
            <TrophyIcon className="text-purple-500 shrink-0 w-4 h-4" />
          </div>

          <div className="min-w-0">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              {contest.isLocked && (
                <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium whitespace-nowrap bg-orange-500/15 text-orange-200 border border-orange-400/20">
                  <IoMdLock />
                  {t("Locked")}
                </span>
              )}

              {ContestService.isFinished(contest) && (
                <span className="inline-flex items-center px-2 py-1 text-[0.75rem] bg-black/40 rounded-full animate-flash">
                  {t("Contest closed")}
                </span>
              )}

              {ContestService.isScoringOpen(contest) && (
                <span className="inline-flex items-center px-2 py-1 text-[0.75rem] bg-black/40 rounded-full animate-flash">
                  {t("Scoring Period")}
                </span>
              )}

              {ContestService.isSubmissionOpen(contest) && (
                <span className="inline-flex items-center px-2 py-1 text-[0.75rem] bg-black/40 rounded-full animate-flash">
                  {t("Submission Period")}
                </span>
              )}

              {ContestService.isBeforeSubmission(contest) && (
                <span className="inline-flex items-center px-2 py-1 text-[0.75rem] bg-black/40 rounded-full animate-flash">
                  {t("Preparations underway")}
                </span>
              )}

              <ContestPeriodLabel contest={contest} locale={locale} />

              {!bulkMode && mode === "settings" && (
                <span
                  className={[
                    "inline-flex items-center rounded-full px-2 py-1 text-[11px] whitespace-nowrap",
                    contest.isPublished
                      ? "bg-violet-500/15 text-violet-200"
                      : "bg-white/10 text-white/50",
                  ].join(" ")}
                >
                  {contest.isPublished ? t("Public") : t("Private")}
                </span>
              )}
            </div>

            <div className="text-[15px] leading-6 text-white break-words">
              {contest.title}
            </div>

            {contest.isLocked && (
              <div className="mt-1 space-y-1 text-[11px] text-orange-100/70">
                <div className="text-orange-200/90">
                  {t("This contest is locked by the administrator")}
                </div>

                {contest.lockReason && (
                  <div>
                    {t("Lock Reason")}:{" "}
                    <span className="text-orange-100/90">{contest.lockReason}</span>
                  </div>
                )}

                {contest.lockedBy && (
                  <div>
                    {t("Locked By")}:{" "}
                    <span className="text-orange-100/90 break-all">{contest.lockedBy}</span>
                  </div>
                )}

                {contest.lockedAt && (
                  <div>
                    {t("Locked At")}:{" "}
                    <span className="text-orange-100/90">{contest.lockedAt}</span>
                  </div>
                )}

                {disableLink && (
                  <div className="text-orange-200/90">
                    {t("You cannot open this contest because it is locked")}
                  </div>
                )}
              </div>
            )}

            {contest.organizer?.userId && (
              <div className="mt-1 text-[11px] text-white/60">
                {t("Organizer")}:{" "}
                <span className="text-white/80">{contest.organizer.userId}</span>
              </div>
            )}

            <CategoryChips
              categories={contest.categories?.map((c) => ({
                id: Number(c.id),
                name: c.name,
              }))}
              label=""
              maxVisible={2}
              testIdPrefix={`contest-${contest.id}-categories`}
            />

            <div className="mt-1 text-[11px] text-white/45 lg:hidden">
              {t("Period")}:{" "}
              {ContestService.getContestPeriodLabelSmart(contest, { locale }) ?? "—"}
            </div>
          </div>

          <div className="hidden lg:flex items-start justify-end min-w-0">
            <div className="text-[11px] text-white/45 text-right leading-5 whitespace-normal break-words">
              {t("Period")}:{" "}
              {ContestService.getContestPeriodLabelSmart(contest, { locale }) ?? "—"}
            </div>
          </div>

          {showActionUI && (
            <div
              className="hidden lg:flex items-start justify-end gap-2"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <div className="flex items-center justify-center h-10">
                <Toggle
                  checked={!!contest.isPublished}
                  disabled={publishSaving || sending}
                  onChange={(next) => onTogglePublish?.(contest, next)}
                />
              </div>

              <button
                type="button"
                aria-label="delete contest"
                disabled={sending || publishSaving}
                onClick={handleDelete}
                className="
                  h-10 w-10
                  flex items-center justify-center
                  rounded-full
                  hover:bg-white/10
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30
                  disabled:opacity-50 disabled:pointer-events-none
                "
              >
                <TrashIcon className="w-4 h-4 text-red-500 hover:text-red-600" />
              </button>
            </div>
          )}
        </a>

        {showActionUI && (
          <div
            className="flex lg:hidden justify-end gap-2 px-1 pb-3"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <div className="flex items-center justify-center h-10">
              <Toggle
                checked={!!contest.isPublished}
                disabled={publishSaving || sending}
                onChange={(next) => onTogglePublish?.(contest, next)}
              />
            </div>

            <button
              type="button"
              aria-label="delete contest"
              disabled={sending || publishSaving}
              onClick={handleDelete}
              className="
                h-10 w-10
                flex items-center justify-center
                rounded-full
                hover:bg-white/10
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30
                disabled:opacity-50 disabled:pointer-events-none
              "
            >
              <TrashIcon className="w-4 h-4 text-red-500 hover:text-red-600" />
            </button>
          </div>
        )}
      </div>
    </li>
  );
};

export default ContestRow;